<script>
/* global axios */
// Modal that lets the admin pick a lookback range and flags, then fires the
// WAHA native `wa/messages pull` command via POST /waha/import_history.
// Reused in two triggers: manual button on WahaStatusCard and the post-scan
// nudge after a successful QR connection.

const LOOKBACK_PRESETS = [
  { value: 30, label: '30 dias', hint: 'Rápido, cobre casos ativos' },
  { value: 90, label: '90 dias', hint: 'Recomendado', recommended: true },
  { value: 180, label: '180 dias', hint: '~6 meses' },
  { value: 365, label: '365 dias', hint: '1 ano' },
  { value: 'all', label: 'Tudo disponível', hint: 'Pode ser lento em contas grandes' },
  { value: 'custom', label: 'Personalizado', hint: '' },
];

export default {
  name: 'WahaHistoryImportModal',

  props: {
    inbox: { type: Object, required: true },
    show: { type: Boolean, default: false },
  },

  emits: ['close', 'imported'],

  data() {
    return {
      presets: LOOKBACK_PRESETS,
      selectedLookback: 90,
      customDays: 60,
      includeMedia: true,
      force: false,
      resolveConversations: false,
      showAdvanced: false,
      preview: null,
      previewLoading: false,
      previewError: null,
      submitting: false,
      submitError: null,
      started: false,
      startedCommand: null,
    };
  },

  computed: {
    accountId() {
      return this.$route.params.accountId;
    },
    effectiveLookback() {
      if (this.selectedLookback === 'custom') return Math.max(1, Number(this.customDays) || 0);
      return this.selectedLookback;
    },
    effectiveLookbackLabel() {
      if (this.selectedLookback === 'all') return 'tudo disponível';
      const days = this.effectiveLookback;
      return `últimos ${days} dia${days === 1 ? '' : 's'}`;
    },
    commandPreview() {
      const days = this.selectedLookback === 'all' ? 3650 : this.effectiveLookback;
      const parts = [`wa/messages pull ${days}d 0d`];
      if (this.includeMedia) parts.push('--media');
      if (this.force) parts.push('--force');
      if (this.resolveConversations) parts.push('--rc');
      return parts.join(' ');
    },
  },

  watch: {
    show: {
      immediate: true,
      handler(open) {
        if (open) {
          this.resetLocalState();
          this.fetchPreview();
        }
      },
    },
    selectedLookback() { this.fetchPreview(); },
    customDays() {
      if (this.selectedLookback === 'custom') this.fetchPreview();
    },
  },

  methods: {
    resetLocalState() {
      this.started = false;
      this.startedCommand = null;
      this.submitError = null;
      this.submitting = false;
    },

    async fetchPreview() {
      this.previewLoading = true;
      this.previewError = null;
      try {
        const lookback = this.selectedLookback === 'all' ? 'all' : this.effectiveLookback;
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/inboxes/${this.inbox.id}/waha/import_history/preview`,
          { params: { lookback_days: lookback } }
        );
        this.preview = data;
      } catch (err) {
        this.previewError = err.response?.data?.error || 'Não foi possível consultar o WAHA';
        this.preview = null;
      } finally {
        this.previewLoading = false;
      }
    },

    async startImport() {
      this.submitting = true;
      this.submitError = null;
      try {
        const lookback = this.selectedLookback === 'all' ? 'all' : this.effectiveLookback;
        const { data } = await axios.post(
          `/api/v1/accounts/${this.accountId}/inboxes/${this.inbox.id}/waha/import_history`,
          {
            lookback_days: lookback,
            include_media: this.includeMedia,
            force: this.force,
            resolve_conversations: this.resolveConversations,
          }
        );
        this.started = true;
        this.startedCommand = data.command;
        this.$emit('imported', data);
      } catch (err) {
        this.submitError = err.response?.data?.error || err.message || 'Erro ao iniciar importação';
      } finally {
        this.submitting = false;
      }
    },

    close() {
      this.$emit('close');
    },
  },
};
</script>

<template>
  <div
    v-if="show"
    class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
    @click.self="close"
  >
    <div class="bg-n-surface-1 border border-n-weak rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
      <!-- Header -->
      <div class="flex items-center justify-between mb-4">
        <div class="flex items-center gap-3">
          <div class="size-10 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
            <span class="i-ri-history-line size-5 text-teal-600 dark:text-teal-400" />
          </div>
          <div>
            <h3 class="text-base font-bold text-n-slate-12 m-0">Importar histórico do WhatsApp</h3>
            <p class="text-xs text-n-slate-11 m-0">Puxa conversas anteriores do seu WhatsApp pra esta caixa</p>
          </div>
        </div>
        <button
          class="size-8 rounded-xl text-n-slate-11 hover:bg-n-slate-3 dark:hover:bg-n-solid-3 flex items-center justify-center"
          @click="close"
        >
          <span class="i-ri-close-line size-5" />
        </button>
      </div>

      <!-- Success state -->
      <div v-if="started" class="text-center py-6">
        <div class="size-16 rounded-full bg-green-500 flex items-center justify-center mx-auto mb-4">
          <span class="i-ri-check-line size-9 text-white" />
        </div>
        <h4 class="text-lg font-medium text-n-slate-12 m-0 mb-2">Importação iniciada</h4>
        <p class="text-sm text-n-slate-11 mb-4">
          O WAHA começou a puxar o histórico. Conversas vão aparecendo aqui
          conforme forem importadas — pode levar alguns minutos.
        </p>
        <div class="rounded-xl bg-n-slate-2 dark:bg-n-solid-2 p-3 text-left mb-4">
          <p class="text-xs text-n-slate-11 m-0 mb-1">Comando enviado:</p>
          <code class="text-xs font-mono text-n-slate-12 break-all">{{ startedCommand }}</code>
        </div>
        <p class="text-xs text-n-slate-11 mb-4">
          Para ver o progresso ao vivo, mande
          <code class="font-mono text-n-slate-12">wa/messages status</code>
          como mensagem de agente em qualquer conversa desta caixa.
        </p>
        <button
          class="px-4 py-2 rounded-xl text-sm font-semibold bg-n-blue-9 text-white hover:bg-n-blue-10"
          @click="close"
        >
          Fechar
        </button>
      </div>

      <!-- Form state -->
      <div v-else>
        <!-- Preview card -->
        <div class="rounded-xl border border-n-weak bg-n-slate-2 dark:bg-n-solid-2 p-3 mb-4 flex items-start gap-3">
          <span class="i-ri-search-line size-4 text-n-slate-11 mt-0.5" />
          <div class="flex-1 text-xs text-n-slate-11">
            <template v-if="previewLoading">
              <span class="i-ri-loader-4-line animate-spin size-3 inline-block mr-1" />
              Consultando o WAHA...
            </template>
            <template v-else-if="previewError">
              <span class="text-red-600 dark:text-red-400">{{ previewError }}</span>
            </template>
            <template v-else-if="preview">
              <strong class="text-n-slate-12">{{ preview.in_window }}</strong>
              conversa(s) individuais nos {{ effectiveLookbackLabel }}
              <span v-if="preview.dm_chats > preview.in_window" class="text-n-slate-11">
                ({{ preview.dm_chats }} total no armazenamento)
              </span>
            </template>
          </div>
        </div>

        <!-- Lookback -->
        <div class="mb-4">
          <label class="text-xs font-semibold text-n-slate-12 uppercase tracking-wide block mb-2">
            Período a importar
          </label>
          <div class="space-y-1">
            <label
              v-for="preset in presets"
              :key="preset.value"
              class="flex items-center gap-3 px-3 py-2 rounded-xl border border-n-weak cursor-pointer hover:bg-n-slate-2 dark:hover:bg-n-solid-2"
              :class="selectedLookback === preset.value ? 'bg-n-blue-9/10 border-n-blue-9' : ''"
            >
              <input
                v-model="selectedLookback"
                type="radio"
                :value="preset.value"
                class="text-n-blue-9 focus:ring-n-blue-9"
              >
              <span class="text-sm font-medium text-n-slate-12 flex-1">{{ preset.label }}</span>
              <span v-if="preset.recommended" class="text-[10px] font-bold uppercase text-green-600 dark:text-green-400">
                Recomendado
              </span>
              <span v-else class="text-xs text-n-slate-11">{{ preset.hint }}</span>
            </label>
          </div>
          <div v-if="selectedLookback === 'custom'" class="mt-2 flex items-center gap-2">
            <input
              v-model.number="customDays"
              type="number"
              min="1"
              max="3650"
              class="flex-1 px-3 py-2 rounded-xl border border-n-weak bg-n-surface-1 text-sm text-n-slate-12"
              placeholder="Dias"
            >
            <span class="text-xs text-n-slate-11">dias atrás</span>
          </div>
        </div>

        <!-- Advanced -->
        <div class="mb-4">
          <button
            type="button"
            class="text-xs font-semibold text-n-blue-9 hover:underline flex items-center gap-1"
            @click="showAdvanced = !showAdvanced"
          >
            <span :class="showAdvanced ? 'i-ri-arrow-down-s-line' : 'i-ri-arrow-right-s-line'" class="size-3" />
            Opções avançadas
          </button>
          <div v-if="showAdvanced" class="mt-2 space-y-2">
            <label class="flex items-start gap-2 text-xs text-n-slate-12 cursor-pointer">
              <input v-model="includeMedia" type="checkbox" class="mt-0.5">
              <div>
                <span class="font-medium">Incluir mídia</span>
                <span class="block text-n-slate-11">Baixa imagens, áudios, vídeos e documentos (mais lento).</span>
              </div>
            </label>
            <label class="flex items-start gap-2 text-xs text-n-slate-12 cursor-pointer">
              <input v-model="force" type="checkbox" class="mt-0.5">
              <div>
                <span class="font-medium">Forçar rebuild</span>
                <span class="block text-n-slate-11">Reconstrói conversas existentes mesmo se já tiverem mensagens.</span>
              </div>
            </label>
            <label class="flex items-start gap-2 text-xs text-n-slate-12 cursor-pointer">
              <input v-model="resolveConversations" type="checkbox" class="mt-0.5">
              <div>
                <span class="font-medium">Resolver conversas após importar</span>
                <span class="block text-n-slate-11">Fecha automaticamente para não poluir a caixa dos agentes.</span>
              </div>
            </label>
          </div>
        </div>

        <!-- Warning -->
        <div class="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3 mb-4 flex items-start gap-2">
          <span class="i-ri-alert-line size-4 text-amber-600 dark:text-amber-400 mt-0.5" />
          <div class="text-xs text-amber-800 dark:text-amber-300">
            Importações grandes podem levar vários minutos e consumir API.
            Apenas conversas individuais (DM) serão importadas — grupos são ignorados.
          </div>
        </div>

        <!-- Command preview -->
        <div class="rounded-xl bg-n-slate-2 dark:bg-n-solid-2 p-2 mb-4">
          <p class="text-[10px] font-semibold text-n-slate-11 uppercase tracking-wide m-0 mb-1">Comando gerado</p>
          <code class="text-xs font-mono text-n-slate-12 break-all">{{ commandPreview }}</code>
        </div>

        <!-- Error -->
        <div
          v-if="submitError"
          class="rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 mb-4 text-xs text-red-800 dark:text-red-300"
        >
          <span class="i-ri-close-circle-line size-4 inline-block mr-1" />
          {{ submitError }}
        </div>

        <!-- Actions -->
        <div class="flex items-center justify-end gap-2 pt-3 border-t border-n-weak">
          <button
            class="px-3 py-1.5 rounded-xl text-xs font-medium border border-n-weak text-n-slate-12 hover:bg-n-slate-2 dark:hover:bg-n-solid-2"
            :disabled="submitting"
            @click="close"
          >
            Cancelar
          </button>
          <button
            class="px-4 py-1.5 rounded-xl text-xs font-semibold bg-n-blue-9 text-white hover:bg-n-blue-10 disabled:opacity-50 flex items-center gap-1"
            :disabled="submitting || previewLoading"
            @click="startImport"
          >
            <span :class="submitting ? 'i-ri-loader-4-line animate-spin' : 'i-ri-download-line'" class="size-3" />
            {{ submitting ? 'Iniciando...' : 'Iniciar importação' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
